<script setup lang="ts">
import { z } from 'zod'
import InputLabel from '@/components/input-label.vue'
import TooltipIcon from '@/shared/components/TooltipIcon.vue'
import InputDate from '@/components/input-date.vue'
import { useYieldUpdate } from '@/features/assets/mutations'
import useNotifications from '@/composables/notifications'
import GridContainer from '@/components/grid-container.vue'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import ModalContainer from '@/components/modal/modal-container.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import ModalFooter from '@/components/modal/modal-footer.vue'
import { faFloppyDisk, faTimes } from '@fortawesome/pro-regular-svg-icons'
import ButtonSecondary from '@/components/button-secondary.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import { Log as BaseLog } from '@/shared/utils/logging/loggers'

const emit = defineEmits<{ close: [] }>()
const props = defineProps<{ assetCode: string }>()
const Log = BaseLog.withContext({ assetCode: props.assetCode })
const { mutateAsync } = useYieldUpdate(props.assetCode)
const { addError, addSuccess } = useNotifications()
const { handleSubmit, isSubmitting } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      actualEndDate: z.coerce.date().max(new Date())
    })
  )
})

const sendUpdate = handleSubmit(
  async values => {
      try {
        await mutateAsync({ actualEndDate: values.actualEndDate })

        addSuccess({ title: 'Guarantee end date updated successfully.' })
        Log.debug('Actual end date updated successfully.')

        emit('close')
      } catch (ex) {
        addError({ title: 'Failed to update guarantee end date' })

        Log.error('Unable to update actual end date', { error: ex })
      }
    }
)

</script>

<template>
  <ModalContainer>
    <ModalTitle>Guarantee Actual End Date</ModalTitle>
    <ModalContent>
      <GridContainer :columns="['max-content', 'auto']" class="align-items-center">
        <InputLabel>
          Guarantee Actual End Date
          <TooltipIcon clas="ms-1" text="The confirmed date when the asset exits the guarantee period and transitions to the warranty period." />
        </InputLabel>
        <InputDate name="actualEndDate" error-variant="tooltip" :max="new Date()" />
      </GridContainer>
    </ModalContent>
    <ModalFooter class="text-end">
      <ButtonSecondary :disabled="isSubmitting" @click="$emit('close')" :icon="faTimes">Cancel</ButtonSecondary>
      <ButtonPrimary @click="sendUpdate" :state="isSubmitting ? 'pending' : undefined" :icon="faFloppyDisk" class="ms-3">Save</ButtonPrimary>
    </ModalFooter>
  </ModalContainer>
</template>
